import { useState, useRef, useEffect } from 'react';
import { localDateStr } from '../utils/dates';

const today = () => new Date();

// Local calendar date (see utils/dates.js) - toISOString() is UTC and
// shifted preset boundaries by a day outside UTC.
function fmt(d) {
  return localDateStr(d);
}

function getPreset(preset) {
  const now = today();
  const y = now.getFullYear();
  const m = now.getMonth();

  switch (preset) {
    case 'mtd':
      return { from: fmt(new Date(y, m, 1)), to: fmt(now) };
    case 'last_month': {
      const first = new Date(y, m - 1, 1);
      const last = new Date(y, m, 0);
      return { from: fmt(first), to: fmt(last) };
    }
    case 'last_3': {
      const first = new Date(y, m - 2, 1);
      const last = new Date(y, m, 0);
      return { from: fmt(first), to: fmt(last) };
    }
    case 'last_6': {
      const first = new Date(y, m - 5, 1);
      const last = new Date(y, m, 0);
      return { from: fmt(first), to: fmt(last) };
    }
    case 'ytd':
      return { from: fmt(new Date(y, 0, 1)), to: fmt(now) };
    case 'last_year':
      return { from: fmt(new Date(y - 1, 0, 1)), to: fmt(new Date(y - 1, 11, 31)) };
    default:
      return { from: '', to: '' };
  }
}

const PRESETS = [
  { key: 'all', label: 'All Time' },
  { key: 'mtd', label: 'MTD' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'last_3', label: 'Last 3 Months' },
  { key: 'last_6', label: 'Last 6 Months' },
  { key: 'ytd', label: 'This Year' },
  { key: 'last_year', label: 'Last Year' },
  { key: 'custom', label: 'Custom…' },
];

export default function PeriodFilter({ value, onChange }) {
  // value: { preset: string, from: string, to: string }
  // onChange: (value) => void
  const [open, setOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState(value?.from || '');
  const [customTo, setCustomTo] = useState(value?.to || '');
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function selectPreset(key) {
    if (key === 'custom') {
      setOpen(true);
      onChange({ preset: 'custom', from: customFrom, to: customTo });
      return;
    }
    if (key === 'all') {
      onChange({ preset: 'all', from: '', to: '' });
      setOpen(false);
      return;
    }
    const range = getPreset(key);
    onChange({ preset: key, from: range.from, to: range.to });
    setOpen(false);
  }

  function applyCustom() {
    onChange({ preset: 'custom', from: customFrom, to: customTo });
    setOpen(false);
  }

  const preset = value?.preset || 'all';
  const label = PRESETS.find(p => p.key === preset)?.label || 'All Time';
  const displayLabel = preset === 'custom' && value?.from
    ? `${value.from} → ${value.to || 'now'}`
    : label;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 12px', borderRadius: 'var(--radius)',
          border: '1px solid var(--border)',
          background: preset !== 'all' ? 'var(--accent)' : 'var(--surface)',
          color: preset !== 'all' ? '#fff' : 'var(--text)',
          fontSize: 13, fontWeight: 500, cursor: 'pointer',
          whiteSpace: 'nowrap'
        }}
      >
        📅 {displayLabel}
        <span style={{ fontSize: 10, opacity: 0.7 }}>▼</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          zIndex: 9999, minWidth: 200, padding: 6
        }}>
          {PRESETS.map(p => (
            <button
              key={p.key}
              onClick={() => selectPreset(p.key)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '7px 12px', border: 'none', borderRadius: 4,
                background: preset === p.key ? 'var(--accent)' : 'none',
                color: preset === p.key ? '#fff' : 'var(--text)',
                fontSize: 13, cursor: 'pointer', fontWeight: preset === p.key ? 600 : 400
              }}
              onMouseEnter={e => { if (preset !== p.key) e.target.style.background = 'var(--bg)'; }}
              onMouseLeave={e => { if (preset !== p.key) e.target.style.background = 'none'; }}
            >
              {p.label}
            </button>
          ))}

          {/* Custom date range */}
          {preset === 'custom' && (
            <div style={{ borderTop: '1px solid var(--border)', padding: '10px 12px', marginTop: 4 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, marginBottom: 6 }}>CUSTOM RANGE</div>
              <div style={{ display: 'grid', gap: 6 }}>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 2 }}>From</label>
                  <input type="date" value={customFrom}
                    onChange={e => setCustomFrom(e.target.value)}
                    style={{ width: '100%', fontSize: 12, padding: '4px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 2 }}>To</label>
                  <input type="date" value={customTo}
                    onChange={e => setCustomTo(e.target.value)}
                    style={{ width: '100%', fontSize: 12, padding: '4px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }} />
                </div>
                <button onClick={applyCustom}
                  style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Helper hook for pages to use period filtering
export function usePeriodFilter(dateField = 'period_start') {
  const [period, setPeriod] = useState({ preset: 'all', from: '', to: '' });

  function filterByPeriod(items) {
    if (!period.from && !period.to) return items;
    return items.filter(item => {
      const val = item[dateField] || item.invoice_date || item.period_start || '';
      if (!val) return true;
      if (period.from && val < period.from) return false;
      if (period.to && val > period.to) return false;
      return true;
    });
  }

  return { period, setPeriod, filterByPeriod };
}
